import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { listParams, usePlayer } from "@/lib/asv";
import { VenueCard, EventCard, GameCard, Banner, Empty } from "@/components/asv/Cards";
import { Filters, ActiveChips } from "@/components/asv/Filters";
import { GameForm } from "@/components/asv/GameForm";
import { NearBox } from "@/components/asv/NearBox";
import { browserLocation } from "@/lib/geo";

const CFG = { venues: ["Venues", VenueCard, "Find a court or pitch and book it."], events: ["Events", EventCard, "Tournaments, leagues and open days."],
  games: ["Games", GameCard, "Join other players, or host your own game."] };

export default function AsvList({ kind }) {
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [creating, setCreating] = useState(false);
  const { player } = usePlayer();
  const f = listParams(sp);
  const [title, Card, sub] = CFG[kind];

  const load = () => { setD(null); api.get(`/asv/${kind}`, { params: f }).then((r) => setD(r.data)).catch(() => setD({ items: [] })); };
  useEffect(load, [kind, sp.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

  const clean = (n) => Object.fromEntries(Object.entries(n).filter(([, x]) => x !== "" && x != null));
  const set = (k, v) => {
    const n = { ...f, [k]: v };
    if (k === "sort" && v === "nearest" && !f.lat) {
      browserLocation().then((p) => setSp(clean({ ...n, ...p }))).catch(() => setSp(clean(n)));
      return;
    }
    setSp(clean(n));
  };

  return (
    <div className="pt-10" data-testid={`asv-list-${kind}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="asv-display text-4xl font-extrabold sm:text-5xl">{title}</h1><p className="asv-muted mt-2 text-sm sm:text-base">{sub}</p></div>
        {kind === "games" && player && <button className="asv-btn" onClick={() => setCreating(true)} data-testid="create-game-open"><Plus className="h-4 w-4" />Create game</button>}
        {kind === "games" && player === null && <a href="/signin?next=/games" className="asv-btn" data-testid="create-game-signin">Sign in to create a game</a>}
      </div>
      {kind !== "games" && <input className="asv-input mt-6 max-w-xl" placeholder={`Search ${kind}`} defaultValue={f.q || ""} onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)} data-testid="list-search" />}
      <NearBox f={f} setLoc={(p) => setSp(clean({ ...f, city: "", ...p }))} />
      <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
        <Filters kind={kind} f={f} set={set} />
        <div className="min-w-0 space-y-5">
          <ActiveChips f={f} set={set} clear={() => setSp({})} />
          <Banner b={d?.banner} />
          {!!d?.sponsored?.length && <div className="grid gap-5 sm:grid-cols-2" data-testid="sponsored-results">{d.sponsored.map((x) => <Card key={`s-${x.key}`} x={x} row />)}</div>}
          {!d ? <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="asv-card h-72 animate-pulse" />)}</div>
            : d.items.length ? <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="list-results">{d.items.map((x) => <Card key={x.key || x.id} x={x} row />)}</div>
              : <Empty text={`No ${kind} match these filters yet.`} />}
        </div>
      </div>
      {creating && <GameForm onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
}
